import * as React from "react";
import { ComponentTypes, DirectoryTreeType } from "../../../config/config";
import Loader from "../../Loader";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../../../redux";
import { getTeams } from "../../../redux/modules/teams";
import { addWorkingDirectory, setWorkingDirectories } from "../../../redux/modules/workingDirectory";
import { LanguageContext } from "../../../../../utilities/LocalizationModule";
import { ActionButton, Spinner, SpinnerSize } from "@fluentui/react";
import { IFolderPanelInfo } from "../../../interfaces/IFolderPanelInfo";
import { initialFolderOptionPanelInfoSetting, validSaveModes } from "../../../config/constants";
import FolderOptionPanel from "../../panels/FolderOptionPanel";
import ExpanderWithSubtitle from "../ExpanderWithSubtitle";
import { expanderConstants } from "../../../redux/models/constants/expanders";
import FileAndFoldersSharepointSearch from "../../file/fileAndFoldersSharepointSearch";
import { isMobile } from "../../../styles/mobileStyles";
import { ISiteInfoLocation } from "../../../interfaces/ISiteInfo";
import authModule from "../../../../../utilities/AuthModule";
import { ITeamChannelLocationDto } from "../../../interfaces/services/ITeamChannelDto";
import { getHubSiteSites, getSharePointDocumentLibraries, getSharePointSubsite, getTeamChannels } from "../../../services/GraphService";
import { generateRandomString } from "../../helpers/randomStringHelper";
import { buildWorkingDirectoryFromTree, calculateLocationHeight, findWorkingDirectories, openTreeFolderOptions } from "../../helpers/treeHelperGeneral";
import { TeamChannelItem } from "../../_v2/components/Explorer/FolderView/TeamChannelItem";
import savingLocationStructureModule from "../../../../../utilities/SavingLocationStructureModule";
import { logger } from "../../../../../utilities/logger";
import generalModule from "../../../../../utilities/GeneralModule";
import { setContentManagerSearchValue, setIsContentManagerContentLoading, toggleContentManagerReload } from "../../../redux/modules/contentManagerDirectory";
import { setSearch } from "../../../redux/modules/search";
import { IPageSettings } from "../../../interfaces/IPageSettings";
import { IFolderLocation, ISharepointDocumentLocation } from "../../../interfaces/ISharepointDocument";
import { useLazyRenderListAsStreamTreeQuery } from "../../_v2/redux/api/sharepoint/api-sharepoint-slice";
import { SharePointFolderItem } from "../../_v2/components/Explorer/FolderView/SharePointFolderItem";
import { setLoading, incrementAmountOfLoaders, decrementAmountOfLoaders } from "../../../redux/modules/loader";
import { ITeam } from "../../../interfaces/services/ITeam";
import { SiteItem } from "../../_v2/components/Explorer/FolderView/SiteItem";
import { SharePointDocumentLibraryItem } from "../../_v2/components/Explorer/FolderView/SharePointDocumentLibraryItem";
import { FolderViewWrapper } from "../../helpers/folderViewWrapper";
import { getListSettings, getUserEffectivePermissions } from "../../../services/SharePointRestServices";
import { setCanViewFolder } from "../../../redux/modules/currentSharepointFolder";
import { SharePointSiteHelper } from "../../../../../utilities/SharePointSiteHelper";
import SubTitleRefreshButton from "../SubTitleRefreshButton";
import { ChevronDoubleDown20Regular } from "../../_v2/components/SvgIcons/ChevronDoubleDown20Regular";

const mapStateToProps = (state: RootState) => {
  return {
    reload: state.reload.toggle,
    searchValue: state.search.searchValue.value,
    teams: state.teams.teams,
    isTeamsLoading: state.teams.isTeamsLoading,
    contentManagerFilterValue: state.contentManagerDirectory.filterValue,
    user: state.user,
    mode: state.mode.mode,
    hasFileSelected: state.files.files.some(file => file.isSelected),
    userSettings: state.user.settings,
    allowedSites: state.tenantInfo.allowedSiteCollections,
    hideDocumentLibrariesInTeams: state.tenantInfo?.hideDocumentLibrariesInTeams,
    tenantSiteConfig: state.tenantInfo?.sites
  };
};

const mapDispatchToProps = {
  addWorkingDirectory,
  setWorkingDirectories,
  getTeams,
  setContentManagerSearchValue,
  setIsContentManagerContentLoading,
  setSearch,
  toggleContentManagerReload,
  setLoading,
  incrementAmountOfLoaders,
  decrementAmountOfLoaders
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface TeamsProps extends PropsFromRedux {
  hide: boolean;
  isGeneralSearchRequest?: boolean;
  isSearchingFilesAndFolders?: boolean;
  hideGeneralSearchElement?: () => void;
}

const Teams: React.FunctionComponent<TeamsProps> = ({
  hide,
  addWorkingDirectory,
  setWorkingDirectories,
  getTeams,
  setContentManagerSearchValue,
  setIsContentManagerContentLoading,
  setSearch,
  reload,
  searchValue,
  teams,
  isTeamsLoading,
  contentManagerFilterValue,
  user,
  mode,
  hasFileSelected,
  toggleContentManagerReload,
  setLoading,
  incrementAmountOfLoaders,
  decrementAmountOfLoaders,
  userSettings,
  allowedSites,
  hideDocumentLibrariesInTeams,
  tenantSiteConfig,
  isGeneralSearchRequest,
  isSearchingFilesAndFolders,
  hideGeneralSearchElement
}: TeamsProps) => {
  const languageStrings = React.useContext(LanguageContext);
  const [folderOptionPanelInfo, setFolderOptionPanelInfo] = React.useState<IFolderPanelInfo>(initialFolderOptionPanelInfoSetting);
  const [isSearchingContent, setIsSearchingContent] = React.useState<boolean>(false);
  const [isFileOrFolderSearchResultsAvailable, setIsFileOrFolderSearchResultsAvailable] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isReloading, setIsReloading] = React.useState<boolean>(false);

  const [allTeams, setAllTeams] = React.useState<ISiteInfoLocation[]>([]);
  const [teamItems, setTeamItems] = React.useState<JSX.Element[]>([]);

  const selectedItem = React.useRef(null);
  const hidden = React.useRef<boolean>(hide);
  const [locationHeight, setLocationHeight] = React.useState<number>(160);

  const [getListAsStreamTree] = useLazyRenderListAsStreamTreeQuery();

  const isFirstRun = React.useRef(true);
  const compareWithCachedTeams = React.useRef(false);
  const allowTeamsScroll = React.useRef<boolean>(false);

  React.useEffect(() => {
    if (!teams) {
      getTeams(false, true);
    }
  }, []);

  React.useEffect(() => {
    hidden.current = hide;
  }, [hide]);

  React.useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }

    if (!hidden.current && !generalModule.isInConentMangerMode()) {
      getTeams(false, false);
    }

  }, [reload]);

  React.useEffect(() => {
    if (teams && teams.length > 0) {
      let filteredTeams: ITeam[];
      if (searchValue && searchValue.trim() !== "") {
        filteredTeams = teams.filter(team => team.displayName?.toLowerCase().includes(searchValue.trim().toLowerCase()));
      }
      else if (contentManagerFilterValue && contentManagerFilterValue.trim() !== "") {
        filteredTeams = teams.filter(team => team.displayName?.toLowerCase().includes(contentManagerFilterValue.trim().toLowerCase()));
      }
      else {
        filteredTeams = teams;
      }

      const allTeamSites: ISiteInfoLocation[] = filteredTeams.map((team, index) => {
        return {
          id: team.id,
          name: team.displayName,
          siteId: team.siteId,
          webUrl: team.webUrl,
          isSubsite: false,
          key: "t" + index,
          teamDriveId: team.driveId,
          teamGroupId: team.id,
          isTeam: true,
          isSite: false,
          canExpand: true,
          expanded: false,
          treeType: DirectoryTreeType.site,
          level: 1,
          isLoading: false,
          loaded: false,
          isSelected: false,
          randomId: generateRandomString(),
          isSharedTeam: team.isSharedTeam
        }
      });

      setAllTeams(prevValues => {
        if (allTeamSites?.length > 0) {
          if (searchValue || contentManagerFilterValue || compareWithCachedTeams.current === false) {
            return allTeamSites;
          }

          const expandedItems: ISiteInfoLocation[] = prevValues?.filter(item => item.expanded) ?? [];
          const newItems: ISiteInfoLocation[] = allTeamSites?.filter(site => !expandedItems.some(x => x.id === site.id));
          const allSites: ISiteInfoLocation[] = expandedItems.concat(newItems);

          return allSites;
        }
        return null;
      });

      compareWithCachedTeams.current = true;
    }
  }, [teams, searchValue, contentManagerFilterValue]);

  React.useEffect(() => {
    allowTeamsScroll.current = (allTeams as any[])?.some(team => team?.expanded
      && (team?.subSites?.some(x => x?.expanded)
        || team?.documents?.some(x => x?.expanded)
        || team?.channels?.some(x => x?.expanded))
    );
    setLocationHeight(calculateLocationHeight(allTeams));
    setTeamItems(
      allTeams && allTeams.length > 0
        ? allTeams.map((item: ISiteInfoLocation) => (
          <div key={item.id}>
            <SiteItem
              item={item}
              isTeamsLocation={true}
              isMobile={isMobile}
              allowScroll={allowTeamsScroll.current}
              onSiteClick={addNextDirectory}
              onExpandCollapseClick={onLocationSitesChevronClicked}
              onOptionsClick={async (site: ISiteInfoLocation) => setFolderOptionPanelInfo(await openTreeFolderOptions(allTeams, site, user))}
            />
            {item.subSites && item.expanded && renderLocationSites(item.subSites, false)}
            {item.channels && item.expanded && renderChannels(item.channels)}
            {item.documents && item.expanded && renderDocuments(item.documents)}
          </div>
        ))
        : (!searchValue
          ? [<div key="0">
            <p className="xb-empty-location-message centerText">
              {contentManagerFilterValue?.length > 0 ? languageStrings.NoResultsFound : languageStrings.NotAssignedToTeam}
            </p>
          </div>]
          : [])
    );
  }, [allTeams]);

  const addNextDirectory = (item: ISiteInfoLocation) => {
    let directoryToAdd: IPageSettings = {
      id: item.id,
      type: ComponentTypes.SharePointDocuments,
      siteId: item.siteId ?? item.id,
      siteDisplayName: item.name,
      webUrl: item.webUrl,
      directory: "",
      DocumentLibrary: "",
      teamGroupId: item.id,
      teamDriveId: item.teamDriveId,
      displayName: item.name,
      isTeamsFolder: true,
      isHub: item.isHub,
      hubId: item.hubId,
      parentHubId: item.parentHubId,
      isSharedTeam: item.isSharedTeam
    };

    if (isGeneralSearchRequest || generalModule.isInConentMangerMode()) {
      if (generalModule.isInConentMangerMode()) setContentManagerSearchValue("");
      if (isGeneralSearchRequest && hideGeneralSearchElement) hideGeneralSearchElement();
      setWorkingDirectories([
        {
          type: ComponentTypes.Teams,
          id: "2",
          directory: "",
          displayName: ComponentTypes.Teams,
          webUrl: "",
        },
        directoryToAdd
      ]);
    }
    else {
      setSearch("");
      addWorkingDirectory(directoryToAdd);
    }
  };

  const isValidDropZone = React.useMemo((): boolean => {
    return validSaveModes.some(item => item === mode) || generalModule.isInConentMangerMode();
  }, [mode]);

  const renderLocationSites = (locationSites: ISiteInfoLocation[], isTeamsLocation: boolean = true) => {
    return SharePointSiteHelper.groupAndSortSites(locationSites)
      .map(item => {
        const isTeamSite = teams?.some(team => team.siteId === item.id || team.siteId === item.siteId);
        return isTeamSite && !userSettings.enableTeams ? null : (
          <div key={item.id}>
            <SiteItem
              item={item}
              isTeamsLocation={isTeamsLocation}
              isMobile={isMobile}
              allowScroll
              onSiteClick={addNextDirectory}
              onExpandCollapseClick={onLocationSitesChevronClicked}
              onOptionsClick={async (site: ISiteInfoLocation) => setFolderOptionPanelInfo(await openTreeFolderOptions(allTeams, site, user))}
            />
            {item.subSites && item.expanded && renderLocationSites(item.subSites, false)}
            {item.channels && item.expanded && renderChannels(item.channels)}
            {item.documents && item.expanded && renderDocuments(item.documents)}
          </div>
        )
      }).filter(item => item);
  }

  const getTreeLocations = React.useCallback((): any[] => {
    return allTeams;
  }, [allTeams]);

  const onLocationSitesChevronClicked = async (item: ISiteInfoLocation, isRefresh: boolean = false) => {
    setLocationItem(item, undefined, true);
    // if (isRefresh || (!item.expanded && (((item.isSite || item.isSubsite) && (!item.documents || item.documents?.length === 0)) || (item.isTeam && (!item.channels || item.channels?.length === 0))))) {
    if (isRefresh || (!item.expanded && !item.loaded)) {
      const level = item.level;

      const accessToken = await authModule.getGraphAccessToken();
      let [subSitesResult, documentLibrariesResult, channelsResult, hubSitesResult] = await Promise.all([
        !item.isSharedTeam ? getSharePointSubsite(accessToken, item.isTeam ? item.siteId : item.id, allowedSites) : { data: [], nextPageLink: null },
        !item.isSharedTeam && (!hideDocumentLibrariesInTeams || !item.isTeam) ? getSharePointDocumentLibraries(accessToken, item.isTeam ? item.siteId : item.id, generalModule.isInConentMangerMode()) : [],
        item.isTeam ? getTeamChannels(accessToken, item.teamGroupId, item.teamDriveId) : [],
        item.isHub ? getHubSiteSites(accessToken, item.hubId, allowedSites) : []
      ]);

      let subSites: ISiteInfoLocation[] = [
        ...(subSitesResult?.data ?? []),
        ...(hubSitesResult ?? [])
      ].map(subSite => {
        return {
          ...subSite,
          name: subSite.displayName,
          isSubsite: !subSite.isHub,
          parentId: item.id,
          treeType: DirectoryTreeType.site,
          canExpand: true,
          expanded: false,
          level: level + 1,
          isLoading: false,
          randomId: generateRandomString()
        }
      });

      const sitesConfig = tenantSiteConfig?.filter(siteInfo => siteInfo.displayName?.trim().toLowerCase() === item.name?.trim().toLowerCase());
      const hiddenDocumentLibraries = Array.isArray(sitesConfig) && sitesConfig?.length > 0 ? sitesConfig[0]?.documentLibraries?.filter(docLib => docLib.hidden === true) : [];

      const documents: ISharepointDocumentLocation[] = documentLibrariesResult
        ?.filter(docLib => !hiddenDocumentLibraries.some(hiddenLib => hiddenLib?.title?.trim().toLowerCase() === docLib?.name?.trim().toLowerCase()))
        ?.map(doc => {
          return {
            ...doc,
            parentId: item.id,
            treeType: DirectoryTreeType.document,
            level: level + 1,
            canExpand: true,
            expanded: false,
            isLoading: false,
            randomId: generateRandomString()
          }
        });

      const channels: ITeamChannelLocationDto[] = channelsResult?.map(channel => {
        return {
          ...channel,
          parentId: item.id,
          treeType: DirectoryTreeType.channel,
          isSelected: false,
          key: item.key,
          level: level + 1,
          randomId: generateRandomString(),
          canExpand: true,
          expanded: false,
          loaded: false
        }
      });

      setLocationsChannelUpdate(item, channels, undefined, undefined, false, isRefresh, documents, subSites);
    }
    else if ((!item.expanded && (item.subSites?.length > 0 || item.documents?.length > 0 || item.channels?.length > 0)) || item.expanded) {
      setLocationsToggleExpanded(item);
    }
  }

  const setContextAwareLoading = React.useCallback((isLoading: boolean, contentManagerOnly: boolean = false) => {
    if (!contentManagerOnly && !generalModule.isInConentMangerMode()) {
      // Drive the global counter (so the full-area overlay in general search covers Teams loading),
      // and keep the local flag (so the in-content-view early-return loader still shows when navigated into a team).
      if (isLoading) incrementAmountOfLoaders(); else decrementAmountOfLoaders();
      setIsLoading(isLoading);
    }
    else {
      setIsContentManagerContentLoading(isLoading);
    }
  }, []);

  const onTreeItemClick = React.useCallback(async (event: React.SyntheticEvent, treeItem: any) => {
    event.stopPropagation();
    setContextAwareLoading(true);
    try {
      setLocationItem(treeItem, true, undefined);
      const newWorkingDirectories = findWorkingDirectories(allTeams, treeItem);
      const { directories } = await buildWorkingDirectoryFromTree(newWorkingDirectories, user);
      if (!generalModule.isInConentMangerMode()) setSearch("");
      setWorkingDirectories(directories);
      if (generalModule.isInConentMangerMode()) {
        setContentManagerSearchValue("");
        toggleContentManagerReload();
      }
    } catch (error) {
      logger(error, "SharePointSite.tsx (onTreeItemClick)");
    }
    setContextAwareLoading(false);
  }, [allTeams, user]);

  const setLocationItem = (location: any, isSelected?: boolean, isLoading?: boolean, isLoadingChannelInfo?: boolean) => {
    setAllTeams((prevLocations) => {
      if (isSelected !== undefined) {
        selectedItem.current = { id: location.id, randomId: location.randomId };
      }

      const recursiveMap = (sites) => {
        return sites.map((site: any) => {
          let updatedSite = site;
          let isCurrentItemSelected =
            selectedItem.current && site.id === selectedItem.current.id && site.randomId === selectedItem.current.randomId;
          if (site.id === location.id && site.randomId === location.randomId) {
            updatedSite = {
              ...site,
              isLoading: isLoading !== undefined ? isLoading : site.isLoading,
              isSelected: isSelected !== undefined ? isSelected : site.isSelected,
              isLoadingChannelInfo: isLoadingChannelInfo !== undefined ? isLoadingChannelInfo : site.isLoadingChannelInfo,
            };
          }
          else if (isCurrentItemSelected) {
            updatedSite = {
              ...site,
              isSelected: isSelected !== undefined ? isSelected : site.isSelected
            };
          }
          else {
            updatedSite = {
              ...site,
              isSelected: false,
            };
          }
          if (site.subSites) {
            updatedSite = {
              ...updatedSite,
              subSites: recursiveMap(site.subSites),
            };
          }
          if (site.channels) {
            updatedSite = {
              ...updatedSite,
              channels: recursiveMap(site.channels),
            };
          }
          if (site.documents) {
            updatedSite = {
              ...updatedSite,
              documents: recursiveMap(site.documents),
            };
          }
          if (site.folders) {
            updatedSite = {
              ...updatedSite,
              folders: recursiveMap(site.folders),
            };
          }
          return updatedSite;
        });
      };

      return recursiveMap(prevLocations);
    });
  };

  const setLocationsChannelUpdate = (location: any, channels: ITeamChannelLocationDto[], folders: IFolderLocation[], foldersNextHref: string, isLoadmore: boolean = false, isRefresh: boolean = false, documents?: ISharepointDocumentLocation[], subsites?: ISiteInfoLocation[]) => {
    setAllTeams(prevLocations => {
      const recursiveFolderUpdate = (items) => {
        return items?.map(item => {
          let updatedItem = item;
          if (item.id === location.id && item.randomId === location.randomId) {
            updatedItem = {
              ...item,
              folders: [...folders],
              foldersNextHref: foldersNextHref ? foldersNextHref : "",
              expanded: (isLoadmore || isRefresh) ? item.expanded : !item.expanded,
              canExpand: folders.length > 0,
              isLoading: false,
              loaded: true,
            };
          }
          else if (item.folders) {
            updatedItem = {
              ...updatedItem,
              folders: recursiveFolderUpdate(item.folders),
            };
          }
          return updatedItem;
        });
      }

      const recursiveUpdate = (sites) => {
        return sites.map(site => {
          let updatedSite = site;
          if (site.id === location.id && site.randomId === location.randomId) {
            updatedSite = {
              ...site,
              subSites: subsites,
              channels: channels,
              //subSitesNextHref: subSitesNextHref ? subSitesNextHref : "",
              documents: (isLoadmore || isRefresh) ? [...documents] : [...(site.documents || []), ...documents],
              expanded: (isLoadmore || isRefresh) ? site.expanded : !site.expanded,
              isLoading: false,
              loaded: true,
            };

            return updatedSite;
          }
          if (site.subSites) {
            updatedSite = {
              ...updatedSite,
              subSites: recursiveUpdate(site.subSites),
            };
          }
          if (site.documents) {
            updatedSite = {
              ...updatedSite,
              documents: recursiveFolderUpdate(site.documents),
            };
          }
          if (site.channels) {
            updatedSite = {
              ...updatedSite,
              channels: recursiveFolderUpdate(site.channels)
            };
          }
          return updatedSite;
        });
      }

      // Call recursiveUpdate directly on prevLocations
      return recursiveUpdate(prevLocations);
    });
  }

  const setLocationsToggleExpanded = (location: any) => {
    setAllTeams(prevLocations => {
      const recursiveMap = (sites) => {
        return sites.map(site => {
          let updatedSite = site;
          if (site.id === location.id && site.randomId === location.randomId) {
            updatedSite = {
              ...site,
              expanded: !site.expanded,
              isLoading: false,
              isLoadingChannelInfo: false
            };
          }
          if (site.subSites) {
            updatedSite = {
              ...updatedSite,
              subSites: recursiveMap(site.subSites),
            };
          }
          if (site.documents) {
            updatedSite = {
              ...updatedSite,
              documents: recursiveMap(site.documents),
            };
          }
          if (site.channels) {
            updatedSite = {
              ...updatedSite,
              channels: recursiveMap(site.channels),
            };
          }
          if (site.folders) {
            updatedSite = {
              ...updatedSite,
              folders: recursiveMap(site.folders),
            };
          }

          return updatedSite;
        });
      }

      // Call recursiveMap directly on prevLocations
      return recursiveMap(prevLocations);
    });
  }

  const onChannelClicked = React.useCallback(async (event: React.SyntheticEvent, item: ITeamChannelLocationDto, isSaveRequest: boolean = false) => {
    if (isSaveRequest) {
      event.preventDefault();
      event.stopPropagation();

      setContextAwareLoading(true);

      try {
        const newWorkingDirectories = findWorkingDirectories(allTeams, item);
        const { directories } = await buildWorkingDirectoryFromTree(newWorkingDirectories, user);

        savingLocationStructureModule.setSavingLocationStructure(directories);

        if (generalModule.isInConentMangerMode()) {
          generalModule.setOpenPanelUploadPanel(true);
          setWorkingDirectories(directories);
        } else {
          const savingDirectory = {
            ...directories[directories.length - 1],
            isSaveLocationStructureSet: true,
            displayName: languageStrings.Properties,
            type: ComponentTypes.Saving
          }

          const newLocations = [
            ...directories,
            savingDirectory,
          ];

          setWorkingDirectories(newLocations);
        }

      } catch (error) {
        logger(error, "Teams.tsx (onChannelClicked)");
      }

      setContextAwareLoading(false);
    }
  }, [allTeams, user]);

  const renderChannels = (channels: ITeamChannelLocationDto[]) => {
    return channels.map((channel, index) => (
      <div key={`${channel.id}-${index}`}>
        <TeamChannelItem
          key={index}
          treeItem={channel}
          isMobile={isMobile}
          isValidDropZone={isValidDropZone}
          inTeamsComponent={true}
          allowScroll
          getTreeLocations={getTreeLocations}
          hasFileSelected={hasFileSelected}
          onChannelClicked={onChannelClicked}
          onTreeItemClick={onTreeItemClick}
          openFolderOptionsTree={async (e) => {
            e.stopPropagation();
            setLocationItem(channel, undefined, undefined, true);
            setFolderOptionPanelInfo(await openTreeFolderOptions(allTeams, channel, user));
            setLocationItem(channel, undefined, undefined, false);
          }}
          onTreeExpand={async (isRefresh) => {
            // if (isRefresh || (!channel.expanded && (!channel.folders || channel.folders?.length === 0))) {
            if (isRefresh || (!channel.expanded && !channel.loaded)) {
              setLocationItem(channel, undefined, true);
              try {
                const newWorkingDirectories = findWorkingDirectories(allTeams, channel);
                const { directories } = await buildWorkingDirectoryFromTree(newWorkingDirectories, user);
                const currDirectory = directories[directories.length - 1];

                const domain = new URL(currDirectory.webUrl).origin

                const level = channel.level;
                let folders: IFolderLocation[] = [];

                const results = await getListAsStreamTree({
                  siteUrl: currDirectory.webUrl,
                  sharePointListId: currDirectory.sharePointListId,
                  accessToken: await authModule.getSharePointAccessToken(),
                  nextPageUrl: "",
                  serverRelativePath: currDirectory.directory ? `${currDirectory.webUrl.replace(domain, "")}/${currDirectory.directory}` : ""
                });

                const foldersNextHref = results?.data?.ListData?.NextHref ? results?.data?.ListData?.NextHref : "";

                if (results?.data?.ListData?.Row && results?.data?.ListData?.Row.length > 0) {
                  folders = results?.data?.ListData?.Row.map(row => {
                    const folderChildCount = row.FolderChildCount ? +row.FolderChildCount : 0;

                    const spItemUrlparts = row['.spItemUrl'].split('/');
                    const itemId = spItemUrlparts[spItemUrlparts.length - 1].split('?')[0];
                    const driveId = spItemUrlparts[spItemUrlparts.length - 3];

                    return {
                      id: itemId,
                      name: row.FileLeafRef,
                      level: level + 1,
                      driveId,
                      sharePointUniqueId: row.UniqueId.replace("{", "").replace("}", ""),
                      contentTypeId: row.ContentTypeId,
                      path: row.FileRef,
                      canExpand: folderChildCount > 0,
                      expanded: false,
                      isLoading: false,
                      isLoadingChannelInfo: false,
                      webUrl: currDirectory.webUrl,
                      sharePointListId: currDirectory.sharePointListId,
                      treeType: DirectoryTreeType.folder,
                      randomId: generateRandomString(),
                      canRefresh: true
                    }
                  })
                }
                setLocationsChannelUpdate(channel, undefined, folders, foldersNextHref, false, isRefresh);
              } catch (error) {
                logger(error, "Teams.tsx (renderChannels(getListAsStreamTree))");
              }
              setLocationItem(channel, undefined, false);
            }
            else if ((!channel.expanded && channel.folders?.length > 0) || channel.expanded) {
              setLocationsToggleExpanded(channel);
            }

          }}
        />
        {channel.folders && channel.expanded && renderFolders(channel.folders, channel)}
      </div>
    ));
  };

  const renderDocuments = (documents: ISharepointDocumentLocation[]) => {
    return documents.map((document, index) => (
      <div key={`${document.id}-${index}`}>
        <SharePointDocumentLibraryItem
          key={index}
          treeItem={document}
          isMobile={isMobile}
          isValidDropZone={isValidDropZone && !document.isList}
          allowScroll
          getTreeLocations={getTreeLocations}
          onTreeItemClick={onTreeItemClick}
          hasFileSelected={hasFileSelected}
          handleSaveClick={handleSaveClick}
          openFolderOptionsTree={async (e) => {
            e.stopPropagation();
            setFolderOptionPanelInfo(await openTreeFolderOptions(allTeams, document, user));
          }}
          onTreeExpand={async (isRefresh) => {
            // if (isRefresh || (!document.expanded && (!document.folders || document.folders?.length === 0))) {
            if (isRefresh || (!document.expanded && !document.loaded)) {
              setLocationItem(document, undefined, true);
              try {
                const newWorkingDirectories = findWorkingDirectories(allTeams, document);
                const { directories } = await buildWorkingDirectoryFromTree(newWorkingDirectories, user);
                const currDirectory = directories[directories.length - 1];

                const level = document.level;
                let folders: IFolderLocation[] = [];

                const results = await getListAsStreamTree({
                  siteUrl: currDirectory.webUrl,
                  sharePointListId: currDirectory.sharePointListId,
                  accessToken: await authModule.getSharePointAccessToken(),
                  nextPageUrl: "",
                  serverRelativePath: ""
                });

                const foldersNextHref = results?.data?.ListData?.NextHref ? results?.data?.ListData?.NextHref : "";

                if (results?.data?.ListData?.Row && results?.data?.ListData?.Row.length > 0) {
                  folders = results?.data?.ListData?.Row.map(row => {
                    const folderChildCount = row.FolderChildCount ? +row.FolderChildCount : 0;

                    const spItemUrlparts = row['.spItemUrl'].split('/');
                    const itemId = spItemUrlparts[spItemUrlparts.length - 1].split('?')[0];
                    const driveId = spItemUrlparts[spItemUrlparts.length - 3];

                    return {
                      id: itemId,
                      name: row.FileLeafRef,
                      level: level + 1,
                      driveId,
                      sharePointUniqueId: row.UniqueId.replace("{", "").replace("}", ""),
                      contentTypeId: row.ContentTypeId,
                      path: row.FileRef,
                      canExpand: folderChildCount > 0,
                      expanded: false,
                      isLoading: false,
                      webUrl: currDirectory.webUrl,
                      sharePointListId: currDirectory.sharePointListId,
                      treeType: DirectoryTreeType.folder,
                      randomId: generateRandomString(),
                      canRefresh: true
                    }
                  })
                }

                setLocationsChannelUpdate(document, undefined, folders, foldersNextHref, false, isRefresh);
              } catch (error) {
                logger(error, "Locations.tsx (renderDocuments(getListAsStreamTree))");
              }
              setLocationItem(document, undefined, false);
            }
            else if ((!document.expanded && document.folders?.length > 0) || document.expanded) {
              setLocationsToggleExpanded(document);
            }

          }}
        />
        {document.folders && document.expanded && renderFolders(document.folders, document)}
      </div>
    ));
  };

  const renderFolders = (folders: IFolderLocation[], location: any) => {
    return (
      <div>
        {folders.map((folder, index) => (
          <div key={`${folder.id}-${index}`}>
            <SharePointFolderItem
              key={index}
              treeItem={folder}
              isMobile={isMobile}
              hasFileSelected={hasFileSelected}
              isValidDropZone={isValidDropZone}
              allowScroll
              getTreeLocations={getTreeLocations}
              onTreeItemClick={onTreeItemClick}
              onSaveClicked={onFolderSaveClicked}
              openFolderOptionsTree={async (e) => {
                e.stopPropagation();
                setLocationItem(folder, undefined, undefined, true);
                setFolderOptionPanelInfo(await openTreeFolderOptions(allTeams, folder, user));
                setLocationItem(folder, undefined, undefined, false);
              }}
              onTreeExpand={async (isRefresh) => {
                // if (isRefresh || (!folder.expanded && (!folder.folders || folder.folders?.length === 0))) {
                if (isRefresh || (!folder.expanded && !folder.loaded)) {
                  setLocationItem(folder, undefined, true);
                  try {
                    const newWorkingDirectories = findWorkingDirectories(allTeams, folder);
                    const { directories } = await buildWorkingDirectoryFromTree(newWorkingDirectories, user);
                    const currDirectory = directories[directories.length - 1];

                    const level = folder.level;
                    let subFolders: IFolderLocation[] = [];


                    const results = await getListAsStreamTree({
                      siteUrl: folder.webUrl,
                      sharePointListId: folder.sharePointListId,
                      accessToken: folder.isOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
                      nextPageUrl: "",
                      serverRelativePath: folder.path
                    });

                    const foldersNextHref = results?.data?.ListData?.NextHref ? results?.data?.ListData?.NextHref : "";

                    if (results?.data?.ListData?.Row && results?.data?.ListData?.Row.length > 0) {
                      subFolders = results?.data?.ListData?.Row.map(row => {
                        const folderChildCount = row.FolderChildCount ? +row.FolderChildCount : 0;

                        const spItemUrlparts = row['.spItemUrl'].split('/');
                        const itemId = spItemUrlparts[spItemUrlparts.length - 1].split('?')[0];
                        const driveId = spItemUrlparts[spItemUrlparts.length - 3];

                        return {
                          id: itemId,
                          name: row.FileLeafRef,
                          level: level + 1,
                          driveId,
                          sharePointUniqueId: row.UniqueId.replace("{", "").replace("}", ""),
                          contentTypeId: row.ContentTypeId,
                          path: row.FileRef,
                          canExpand: folderChildCount > 0,
                          expanded: false,
                          isLoading: false,
                          isLoadingChannelInfo: false,
                          webUrl: currDirectory.webUrl,
                          sharePointListId: currDirectory.sharePointListId,
                          treeType: DirectoryTreeType.folder,
                          isOneDrive: folder.isOneDrive,
                          randomId: generateRandomString(),
                          canRefresh: true
                        }
                      })
                    }

                    setLocationsChannelUpdate(folder, undefined, subFolders, foldersNextHref, false, isRefresh);

                  } catch (error) {
                    logger(error, "Locations.tsx (renderFolders(getListAsStreamTree))");
                  }
                  setLocationItem(folder, undefined, false);
                }
                else if ((!folder.expanded && folder.folders?.length > 0) || folder.expanded) {
                  setLocationsToggleExpanded(folder);
                }
              }}
            />
            {index === folders.length - 1 && location.foldersNextHref && <div style={{ paddingLeft: (location.level ? location.level + 1 : 1) * 15 + 20 }}>
              {!location.isLoading && <ActionButton onRenderIcon={() => <ChevronDoubleDown20Regular />} allowDisabledFocus onClick={async (e: any) => {
                e.stopPropagation();
                setLocationItem(location, undefined, true);
                const newWorkingDirectories = findWorkingDirectories(allTeams, location);
                const { directories } = await buildWorkingDirectoryFromTree(newWorkingDirectories, user);
                const currDirectory = directories[directories.length - 1];

                const domain = new URL(currDirectory.webUrl).origin

                try {
                  const level = location.level ? location.level : 0;
                  let folders: IFolderLocation[] = [];

                  const inOneDrive = location.isOneDrive;
                  const webUrl = currDirectory.webUrl;
                  const sharePointListId = currDirectory.sharePointListId;

                  const results = await getListAsStreamTree({
                    siteUrl: webUrl,
                    sharePointListId,
                    accessToken: await authModule.getSharePointAccessToken(),
                    nextPageUrl: location.foldersNextHref,
                    serverRelativePath: currDirectory.serverRelativePath
                  });

                  const foldersNextHref = results?.data?.ListData?.NextHref ? results?.data?.ListData?.NextHref : "";

                  if (results?.data?.ListData?.Row && results?.data?.ListData?.Row.length > 0) {
                    folders = results?.data?.ListData?.Row.map(row => {
                      const spItemUrlparts = row['.spItemUrl'].split('/');
                      const itemId = spItemUrlparts[spItemUrlparts.length - 1].split('?')[0];
                      const driveId = spItemUrlparts[spItemUrlparts.length - 3];

                      return {
                        id: itemId,
                        name: row.FileLeafRef,
                        level: level + 1,
                        driveId,
                        sharePointUniqueId: row.UniqueId.replace("{", "").replace("}", ""),
                        contentTypeId: row.ContentTypeId,
                        path: row.FileRef,
                        canExpand: true,
                        expanded: false,
                        isLoading: false,
                        webUrl,
                        sharePointListId,
                        isOneDrive: inOneDrive,
                        treeType: DirectoryTreeType.folder,
                        randomId: generateRandomString(),
                        canRefresh: true
                      }
                    })
                  }

                  setLocationsChannelUpdate(location, undefined, [...location.folders, ...folders], foldersNextHref, true);

                } catch (error) {
                  logger(error, "Locations.tsx (renderChannels(getListAsStreamTree))");
                  setLocationItem(location, undefined, false);
                }
              }}>
                {languageStrings.LoadMore}
              </ActionButton>}

            </div>}
            {index === folders.length - 1 && location.foldersNextHref && location.isLoading && <div style={{ paddingTop: "10px" }}>
              <Spinner size={SpinnerSize.small} />
            </div>}

            {folder.folders && folder.expanded && renderFolders(folder.folders, folder)}
          </div>
        ))}
      </div>
    )
  };

  const handleSaveClick = React.useCallback(async (item, e) => {
    e.preventDefault();
    e.stopPropagation();
    e.persist();

    setContextAwareLoading(true);

    try {
      const newWorkingDirectories = findWorkingDirectories(allTeams, item);
      const { directories } = await buildWorkingDirectoryFromTree(newWorkingDirectories, user);
      const currentDir = directories[directories.length - 1];
      const parent = newWorkingDirectories.find(x => x.id === item.parentId);

      var folderViewWrapper = new FolderViewWrapper(parent.subSites, { ...currentDir, DocumentLibrary: item.name, sharePointListId: item.sharePointListId });
      const canViewFolder = folderViewWrapper.canViewFolder();
      const accessToken = await authModule.getSharePointAccessToken();
      const webUrl = currentDir?.webUrl;
      const [settings, permissions] = await Promise.all([
        getListSettings(accessToken, webUrl, item.sharePointListId),
        getUserEffectivePermissions(accessToken, webUrl, item.sharePointListId)
      ]);

      setCanViewFolder({
        sharepointListId: item.sharePointListId,
        canView: canViewFolder,
        canCreateFolder: canViewFolder ? settings?.EnableFolderCreation : false,
        contentTypesEnabled: settings?.ContentTypesEnabled,
        enableMinorVersions: settings?.EnableMinorVersions,
        disableOpenInApp: settings?.ExcludeFromOfflineClient === true,
        userPermissions: permissions
      });

      savingLocationStructureModule.setSavingLocationStructure(directories);

      if (generalModule.isInConentMangerMode()) {
        generalModule.setOpenPanelUploadPanel(true);
        setWorkingDirectories(directories);
        setContentManagerSearchValue("");
        toggleContentManagerReload();
      } else {
        const savingDirectory = {
          ...directories[directories.length - 1],
          isSaveLocationStructureSet: true,
          displayName: languageStrings.Properties,
          type: ComponentTypes.Saving
        }

        const newLocations = [
          ...directories,
          savingDirectory,
        ];

        setWorkingDirectories(newLocations);
      }
    } catch (error) {
      logger(error, "Teams.tsx (handleSaveClick)");
    }

    setSearch("");
    setContextAwareLoading(false);
  }, [allTeams, user]);

  const onFolderSaveClicked = React.useCallback(async (item: any, e: any) => {
    e.preventDefault();
    e.stopPropagation();
    e.persist();

    setLoading(true);
    try {
      const newWorkingDirectories = findWorkingDirectories(allTeams, item);
      const { directories } = await buildWorkingDirectoryFromTree(newWorkingDirectories, user);

      savingLocationStructureModule.setSavingLocationStructure(directories);

      if (generalModule.isInConentMangerMode()) {
        generalModule.setOpenPanelUploadPanel(true);
        setWorkingDirectories(directories);
      } else {
        const savingDirectory = {
          ...directories[directories.length - 1],
          isSaveLocationStructureSet: true,
          displayName: languageStrings.Properties,
          type: ComponentTypes.Saving
        }

        const newLocations = [
          ...directories,
          savingDirectory,
        ];

        setWorkingDirectories(newLocations);
      }
    } catch (error) {
      logger(error, "Teams.tsx (onFolderSaveClicked)");
    }
    setLoading(false);
  }, [allTeams, user]);

  const dismissFolderOptionPanel = React.useCallback(() => {
    setFolderOptionPanelInfo(initialFolderOptionPanelInfoSetting);
  }, []);

  return isTeamsLoading || isLoading ? (
    <Loader hide={hide} />
  ) : (
    <div className={hide ? "none" : (!isGeneralSearchRequest ? "fullHeight" : undefined)}>
      {(!searchValue || (!isSearchingContent && !isSearchingFilesAndFolders && teamItems?.length > 0)) && <ExpanderWithSubtitle
        items={teamItems}
        title={"Teams"}
        expanderNameToToggle={expanderConstants.teams.joinedTeams}
        hasShowMoreButton={searchValue && isFileOrFolderSearchResultsAvailable}
        fixedHeight={locationHeight}
        isWaitingOnContent={isReloading}
        allowScroll={allowTeamsScroll.current}
        actionButton={generalModule.isInConentMangerMode() &&
          <SubTitleRefreshButton
            onClick={async () => {
              setIsReloading(true);
              compareWithCachedTeams.current = false;
              await getTeams(true, false);
              setIsReloading(false);
            }}
          />
        }
      />}
      {folderOptionPanelInfo?.isOpen && <FolderOptionPanel info={folderOptionPanelInfo} dismissPanel={dismissFolderOptionPanel} />}
      {!hide && !isGeneralSearchRequest && searchValue && <FileAndFoldersSharepointSearch parentViewHasItems={teamItems?.length > 0} isSearchingCallback={(isSearching: boolean) => setIsSearchingContent(isSearching)} searchResultsAvailableCallback={(resultsAvailable: boolean) => setIsFileOrFolderSearchResultsAvailable(resultsAvailable)} isLoadingCallback={(isLoading: boolean) => setIsLoading(isLoading)} />}
    </div>
  );
};

export default React.memo(connector(Teams));
